import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';
import apiClient, { setAuthToken } from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

const LoginForm = ({ onLoginSuccess, onBack, onShowRegistrasi }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email harus diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password harus diisi';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password minimal 6 karakter';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    
    try {
      // Panggil API login
      const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, {
        email: formData.email.trim(),
        password: formData.password
      });
      
      // Handle response dari API
      // Response format bisa: { access_token, refresh_token, user } atau { data: { access_token, ... } }
      let accessToken = null;
      
      if (response.access_token) {
        accessToken = response.access_token;
      } else if (response.data && response.data.access_token) {
        accessToken = response.data.access_token;
      }
      
      if (!accessToken) {
        throw new Error('Token tidak ditemukan dalam response');
      }
      
      // Simpan token
      setAuthToken(accessToken);
      
      // Simpan refresh token jika ada
      const refreshToken = response.refresh_token || response.data?.refresh_token;
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      
      // Simpan data user jika ada
      const userData = response.user || response.data?.user;
      if (userData) {
        localStorage.setItem('user_data', JSON.stringify(userData));
      }
      
      // If onLoginSuccess prop is provided, use it, otherwise navigate based on role
      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        // Cek role user untuk menentukan redirect
        // Role ada di data.role.id sesuai struktur response API
        const userRole = userData?.role?.id;
        
        // Jika role adalah RL00003, redirect ke mitra-area
        if (userRole === 'RL00003') {
          navigate('/mitra-area');
        } else {
          // Default redirect ke member-area
          navigate('/member-area');
        }
      }
      
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle error berdasarkan status code
      if (error.status === 401 || error.status === 400) {
        setErrors({
          general: error.data?.message || error.message || 'Email atau password salah'
        });
      } else if (error.status === 422) {
        // Validation error
        const validationErrors = error.data?.detail || [];
        if (validationErrors.length > 0) {
          const errorMessages = validationErrors.map(err => err.msg).join(', ');
          setErrors({
            general: errorMessages
          });
        } else {
          setErrors({
            general: error.data?.message || 'Data tidak valid'
          });
        }
      } else {
        setErrors({
          general: error.message || 'Terjadi kesalahan saat login. Silakan coba lagi.'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-poppins">
      {/* Mobile Wrapper - Exact same as member area */}
      <div className="max-w-sm mx-auto bg-white shadow-2xl min-h-screen">
        
        {/* Header - Exact same as member area */}
        <header className="px-4 pt-6 pb-4 bg-white">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Logo size="small" showText={true} />
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <div className="text-right">
                  <p className="text-sm font-semibold text-primary">Login Required</p>
                  <p className="text-xs text-gray-500">Silakan masuk</p>
                </div>
                <div className="w-10 h-10 rounded-full border-2 border-accent bg-gray-100 flex items-center justify-center">
                  <i className="fa-solid fa-user text-primary"></i>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content - Replace member area content with login form */}
        <main className="pb-24 bg-white">
          {/* Login Form Section */}
          <section className="px-4 mt-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-primary mb-2">Selamat Datang</h1>
              <p className="text-gray-600 text-sm">Masuk ke akun PAK MoO Anda</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <div className="flex items-center space-x-2">
                  <i className="fa-solid fa-exclamation-circle text-red-500"></i>
                  <span className="text-red-700 text-sm">{errors.general}</span>
                </div>
              </div>
            )}

            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fa-solid fa-envelope text-gray-400"></i>
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent transition-colors ${
                    errors.email ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:border-accent'
                  }`}
                  placeholder="Masukkan email Anda"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fa-solid fa-lock text-gray-400"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent transition-colors ${
                    errors.password ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:border-accent'
                  }`}
                  placeholder="Masukkan password Anda"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                />
                <span className="ml-2 text-sm text-gray-600">Ingat saya</span>
              </label>
              <button
                type="button"
                className="text-sm text-accent hover:text-primary font-semibold transition-colors"
              >
                Lupa password?
              </button>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-secondary hover:bg-primary text-white font-bold py-3 px-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Memproses...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-sign-in-alt"></i>
                  <span>Masuk</span>
                </>
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="text-center mt-6">
            <p className="text-gray-600 text-sm">
              Belum punya akun?{' '}
              <button 
                type="button"
                onClick={onShowRegistrasi}
                className="text-accent hover:text-primary font-semibold transition-colors underline"
              >
                Daftar sekarang
              </button>
            </p>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-4">
            <button
              onClick={onBack || (() => navigate('/'))}
              className="text-gray-500 hover:text-primary text-sm transition-colors flex items-center justify-center space-x-2"
            >
              <i className="fa-solid fa-arrow-left"></i>
              <span>Kembali ke Beranda</span>
            </button>
          </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default LoginForm;
